import { useContext, useMemo, useState } from 'react';
import { Edit, Close } from '@mui/icons-material';
import { useQuery } from 'react-query';
import { Table, Pagination, MobileTable } from '../../../components';
import { IResponse, IClient, ISortDirection } from '../../../models';
import { getClients } from '../../../fetch';
import { SALES_PORTAL_PER_PAGE_OPTIONS, getEnvironment, unCamelCase } from '../../../helpers';
import { Button, Box, Typography, useMediaQuery } from '@mui/material';
import { ClientsFilters } from './clients-filters';
import { useHistory } from 'react-router-dom';
import { SalesContext, update } from '../../../context/sales';

const PAGE_NAME = 'Client-List';
export const ClientsList = () => {
  const history = useHistory();
  const removeStickyHeader = useMediaQuery('(max-width: 1012px)');
  const [page, setPage] = useState<number>(0);
  const { perPageSalesPortalValues, dispatchPerPage } = useContext(SalesContext);
  const currentPageState = perPageSalesPortalValues.find(
    state => state?.pageName === PAGE_NAME
  )?.itemsPerPage;
  const perPage = useMemo<number>(
    () => (currentPageState && currentPageState >= 25 ? currentPageState : 25),
    [currentPageState]
  );
  const [recordCount, setRecordCount] = useState<number>(0);
  const [searchValue, setSearch] = useState<string>('');
  const [searchedValue, setSearchedValue] = useState<string>('');
  const [hasAppliedFilters, setHasAppliedFilters] = useState<boolean>(false);
  const [isIncludeComplete, setIncludeComplete] = useState<boolean>(false);
  const [isIncludeHiddenPurchasers, setIncludeHiddenPurchasers] = useState<boolean>(false);
  const isMobile = useMediaQuery('(max-width: 960px)');

  const [filters, setFilters] = useState<{
    query?: string;
  } | null>({});
  const [selectedSort, setSelectedSort] = useState<string>('FirstName');
  const [sortDirection, setSortDirection] = useState<{
    FirstName?: ISortDirection;
    LastName?: ISortDirection;
    BuildNeighborhood?: ISortDirection;
    BuildLot?: ISortDirection;
    BuildStatus?: ISortDirection;
  }>({
    FirstName: 'Asc'
  });
  const {
    isLoading,
    data: clientsData,
    refetch: fetchClients
  } = useQuery<IResponse<IClient[]>, Error>(
    [
      'getClients',
      page,
      perPage,
      filters,
      isIncludeComplete,
      selectedSort,
      sortDirection,
      isIncludeHiddenPurchasers
    ],
    () =>
      getClients({
        page: page + 1,
        perPage,
        search: filters?.query,
        includeComplete: isIncludeComplete,
        sortBy: selectedSort,
        sortDirection: sortDirection[selectedSort] as string,
        includeHiddenPurchasers: isIncludeHiddenPurchasers
      }),
    {
      onSuccess: data => {
        setRecordCount(data.totalRecordCount);
      },
      notifyOnChangeProps: 'tracked'
    }
  );

  const data = useMemo(() => clientsData?.records ?? [], [clientsData]);
  const columns = useMemo(() => {
    const handleClickColumn = (column: string) => {
      setSelectedSort(column);
      setSortDirection({
        ...sortDirection,
        [column]: sortDirection[column] === 'Asc' ? 'Desc' : 'Asc'
      });
    };
    return [
      {
        Header: 'First Name',
        accessor: 'firstName',
        isServerSorted: selectedSort === 'FirstName',
        isServerSortedDesc: sortDirection.FirstName === 'Desc',
        handleClickColumn: () => handleClickColumn('FirstName')
      },
      {
        Header: 'Last Name',
        accessor: 'lastName',
        isServerSorted: selectedSort === 'LastName',
        isServerSortedDesc: sortDirection.LastName === 'Desc',
        handleClickColumn: () => handleClickColumn('LastName')
      },
      {
        Header: 'Neighborhood',
        accessor: 'build.neighborhoodDescription',
        isServerSorted: selectedSort === 'BuildNeighborhood',
        isServerSortedDesc: sortDirection.BuildNeighborhood === 'Desc',
        handleClickColumn: () => handleClickColumn('BuildNeighborhood')
      },
      {
        Header: 'Lot',
        accessor: 'build.lotNumber',
        isServerSorted: selectedSort === 'BuildLot',
        isServerSortedDesc: sortDirection.BuildLot === 'Desc',
        handleClickColumn: () => handleClickColumn('BuildLot')
      },
      {
        Header: 'Status',
        accessor: 'build.statusText',
        isServerSorted: selectedSort === 'BuildStatus',
        isServerSortedDesc: sortDirection.BuildStatus === 'Desc',
        handleClickColumn: () => handleClickColumn('BuildStatus'),
        Cell: props => (
          <>
            {props.cell.row?.original?.build?.statusText
              ? unCamelCase(props.cell.row?.original?.build?.statusText)
              : ''}
          </>
        )
      },
      {
        id: 'actions',
        accessor: '',
        Header: '',
        sort: false,
        Cell: ({
          cell: {
            row: { original }
          }
        }: {
          cell: { row: { original: IClient } };
        }) => {
          return (
            <Box display="flex">
              <Button
                id="edit-county"
                type="button"
                color="primary"
                startIcon={<Edit />}
                onClick={() => {
                  history.push(`/sales/contracting/purchasers/${original.personId}`);
                }}
              >
                Edit
              </Button>
            </Box>
          );
        }
      }
    ];
  }, [sortDirection, selectedSort, history]);
  return (
    <>
      <ClientsFilters
        isLoading={isLoading}
        setHasAppliedFilters={setHasAppliedFilters}
        hasAppliedFilters={hasAppliedFilters}
        setSearchValue={setSearch}
        setSearchedValue={setSearchedValue}
        searchValue={searchValue}
        setIncludeComplete={val => {
          setIncludeComplete(val);
          setPage(0);
        }}
        setIncludeHiddenPurchasers={val => {
          setIncludeHiddenPurchasers(val);
          setPage(0);
        }}
        isIncludeComplete={isIncludeComplete}
        isIncludeHiddenPurchasers={isIncludeHiddenPurchasers}
        handleSearch={(val: string) => {
          setPage(0);
          setSearchedValue(val);
          setHasAppliedFilters(true);
          setFilters({
            ...filters,
            query: val
          });
        }}
        applyFilters={(clearFilters?: boolean, clearSearch?: boolean) => {
          if (clearFilters) {
            setFilters({
              query: ''
            });
          } else {
            setPage(0);
            setFilters({
              ...filters,
              query: searchValue
            });
          }
        }}
      />
      {searchedValue && (
        <Box mb={0.5} display="flex" alignItems="center">
          <Typography color="secondary">Showing results for "{searchedValue}"</Typography>
          <Button
            id="clear-search"
            size="small"
            startIcon={<Close />}
            onClick={() => {
              setSearch('');
              setSearchedValue('');
              setPage(0);
              setFilters({
                query: ''
              });
              fetchClients();
            }}
          >
            Clear Search
          </Button>
        </Box>
      )}
      <Table
        stickyHeaders={removeStickyHeader ? false : true}
        columns={columns}
        data={data}
        isLoading={isLoading}
        hidePagination
        mobileProps={{
          fields: columns
            .filter(c => c.Header)
            .map(c => ({
              name: c.Header,
              accessor: c.accessor,
              Cell: c?.Cell
            })),
          handleEdit: (val: IClient) =>
            history.push(`/sales/contracting/purchasers/${val.personId}`)
        }}
        ResponsiveComponent={isMobile ? MobileTable : undefined}
      />
      {!isLoading && (
        <Pagination
          page={page}
          count={recordCount}
          rowsPerPage={perPage}
          setPage={setPage}
          rowsPerPageOptions={SALES_PORTAL_PER_PAGE_OPTIONS}
          setRowsPerPage={itemsPerPage => {
            return dispatchPerPage(
              update({
                pageName: PAGE_NAME,
                environment: getEnvironment(),
                itemsPerPage: itemsPerPage
              })
            );
          }}
        />
      )}
    </>
  );
};
